/**
 * Routing Chain Visualization UI
 * Interactive tool to preview and verify AI routing decisions
 */

import { BaseComponent } from './components/base-component';
import type { APIClient } from './api';
import type {
  ChainVisualizationRequest,
  ChainVisualizationResponse,
  ChainVerificationResponse,
  ChainStage,
  ModelState
} from './types';

export class RoutingChainVisualizerUI extends BaseComponent {
  private apiClient: APIClient;
  private currentVisualization: ChainVisualizationResponse | null = null;
  private loading = false;

  constructor(container: HTMLElement, apiClient: APIClient) {
    super(container);
    this.apiClient = apiClient;
  }

  async render(): Promise<void> {
    this.container.innerHTML = `
      <div class="routing-chain-ui">
        <div class="routing-header">
          <h2>🔍 AI Routing Chain Visualizer</h2>
          <p class="subtitle">Preview routing decisions before execution</p>
        </div>

        <div class="routing-controls">
          <div class="form-row">
            <div class="form-group">
              <label>Task Type</label>
              <select id="task-type">
                <option value="consenger/conversation">Conversation</option>
                <option value="consenger/translation">Translation</option>
                <option value="consenger/definition">Definition</option>
                <option value="consenger/summary">Summary</option>
                <option value="consenger/grammar">Grammar</option>
                <option value="consenger/context">Context</option>
              </select>
            </div>

            <div class="form-group">
              <label>Region</label>
              <select id="region">
                <option value="US">United States (US)</option>
                <option value="JP">Japan (JP)</option>
                <option value="CN">China (CN)</option>
                <option value="GB">United Kingdom (GB)</option>
                <option value="DE">Germany (DE)</option>
                <option value="KR">South Korea (KR)</option>
                <option value="SG">Singapore (SG)</option>
              </select>
            </div>

            <div class="form-group">
              <label>Tier</label>
              <select id="tier">
                <option value="FREE">FREE</option>
                <option value="PRO">PRO</option>
              </select>
            </div>

            <div class="form-group">
              <label>Preference</label>
              <select id="preference">
                <option value="default">Default</option>
                <option value="quality">Quality</option>
                <option value="cost">Cost</option>
                <option value="speed">Speed</option>
              </select>
            </div>
          </div>

          <div class="form-actions">
            <button id="visualize-btn" class="btn btn-primary">
              🔍 Visualize Chain
            </button>
            <button id="verify-btn" class="btn btn-secondary" disabled>
              ✅ Verify with Real Request
            </button>
          </div>
        </div>

        <div id="visualization-container" class="visualization-container">
          <div class="empty-state">
            <p>👆 Select parameters and click "Visualize Chain" to see routing flow</p>
          </div>
        </div>

        <div id="verification-container" class="verification-container" style="display: none;">
          <!-- Verification results will be shown here -->
        </div>
      </div>
    `;

    // Attach event listeners
    document.getElementById('visualize-btn')?.addEventListener('click', () => this.visualizeChain());
    document.getElementById('verify-btn')?.addEventListener('click', () => this.verifyChain());
  }

  private async visualizeChain(): Promise<void> {
    const taskType = (document.getElementById('task-type') as HTMLSelectElement).value;
    const region = (document.getElementById('region') as HTMLSelectElement).value;
    const tier = (document.getElementById('tier') as HTMLSelectElement).value;
    const preference = (document.getElementById('preference') as HTMLSelectElement).value;

    const container = document.getElementById('visualization-container');
    if (!container) return;

    this.loading = true;
    container.innerHTML = '<div class="loading">⏳ Computing routing chain...</div>';

    try {
      const response = await this.apiClient.get<ChainVisualizationResponse>(
        `/v1/admin/routing/visualize?taskType=${encodeURIComponent(taskType)}&region=${encodeURIComponent(region)}&tier=${encodeURIComponent(tier)}&preference=${encodeURIComponent(preference)}`
      );

      this.currentVisualization = response.data;
      this.renderVisualization(response.data);

      // Enable verify button
      const verifyBtn = document.getElementById('verify-btn') as HTMLButtonElement;
      if (verifyBtn) verifyBtn.disabled = false;
    } catch (error) {
      container.innerHTML = `
        <div class="error">
          <p>❌ Failed to visualize routing chain</p>
          <p class="error-detail">${error instanceof Error ? error.message : 'Unknown'}</p>
        </div>
      `;
    } finally {
      this.loading = false;
    }
  }

  private renderVisualization(data: ChainVisualizationResponse): void {
    const container = document.getElementById('visualization-container');
    if (!container) return;

    container.innerHTML = `
      <div class="chain-flow">
        ${data.stages.map((stage, index) => this.renderStage(stage, index)).join('<div class="stage-arrow">↓</div>')}

        <div class="final-result">
          <h3>⭐ Final Selection</h3>
          <div class="final-model">
            <div class="model-badge">
              ${this.escapeHtml(data.finalModel.displayName)}
            </div>
            <div class="model-provider">${this.escapeHtml(data.finalModel.provider)}</div>
            <div class="model-reason">${this.escapeHtml(data.finalModel.reason)}</div>
            ${data.fallbackAttempts > 0 ? `<div class="fallback-notice">⚠️ Required ${data.fallbackAttempts} fallback attempt(s)</div>` : ''}
          </div>
        </div>
      </div>

      <div class="chain-summary">
        <h4>📊 Summary</h4>
        <ul>
          <li><strong>Task:</strong> ${this.escapeHtml(data.request.taskType)}</li>
          <li><strong>Region:</strong> ${this.escapeHtml(data.request.region)}</li>
          <li><strong>Tier:</strong> ${this.escapeHtml(data.request.tier)}</li>
          <li><strong>Total Models Evaluated:</strong> ${this.countTotalModels(data.stages)}</li>
          <li><strong>Models Filtered:</strong> ${this.countFilteredModels(data.stages)}</li>
          <li><strong>Final Models Available:</strong> ${data.stages[2].passedModels.length}</li>
        </ul>
      </div>
    `;
  }

  private renderStage(stage: ChainStage, index: number): string {
    const isEmpty = stage.models.length === 0;
    const isTierStage = stage.stageNumber === 3;

    return `
      <div class="chain-stage">
        <div class="stage-header">
          <h3>Stage ${stage.stageNumber}: ${this.escapeHtml(stage.stageName)}</h3>
          <p class="stage-description">${this.escapeHtml(stage.description)}</p>
        </div>

        ${isTierStage && stage.metadata?.comparison ? `
          <div class="tier-comparison">
            <div class="comparison-header">
              <strong>Tier Enforcement Comparison:</strong>
            </div>
            <div class="comparison-stats">
              <div class="comparison-item">
                <span class="label">Models from Stage 2:</span>
                <span class="value">${stage.metadata.comparison.totalFromStage2}</span>
              </div>
              <div class="comparison-item">
                <span class="label">Tier Allowed Models:</span>
                <span class="value">${stage.metadata.comparison.totalAllowedInTier}</span>
              </div>
              <div class="comparison-item">
                <span class="label">Intersection:</span>
                <span class="value ${stage.metadata.comparison.intersection === 0 ? 'warning' : 'success'}">${stage.metadata.comparison.intersection}</span>
              </div>
            </div>
            <p class="comparison-explanation">${this.escapeHtml(stage.metadata.comparison.explanation)}</p>

            ${stage.metadata.allowedModelsDetails && stage.metadata.allowedModelsDetails.length > 0 ? `
              <div class="tier-allowed-list">
                <strong>Tier Allowed Models:</strong>
                <div class="allowed-models-chips">
                  ${stage.metadata.allowedModelsDetails.map((m: any) => `
                    <span class="chip ${m.inStage2 ? 'in-stage2' : 'not-in-stage2'}" title="${m.inStage2 ? 'Present in Stage 2' : 'Not in Stage 2'}">
                      ${m.inStage2 ? '✓' : '✗'} ${this.escapeHtml(m.displayName)}
                    </span>
                  `).join('')}
                </div>
              </div>
            ` : ''}
          </div>
        ` : ''}

        <div class="stage-content">
          ${isEmpty ?
        '<p class="no-models">✓ Not needed - previous stages succeeded</p>' :
        `
            <div class="models-list">
              ${stage.models.map(model => this.renderModel(model)).join('')}
            </div>
            <div class="stage-stats">
              <span class="stat-badge passed">✓ ${stage.passedModels.length} passed</span>
              ${stage.filteredModels.length > 0 ? `<span class="stat-badge filtered">✗ ${stage.filteredModels.length} filtered</span>` : ''}
            </div>
            `
      }
        </div>
      </div>
    `;
  }

  private renderModel(model: ModelState): string {
    const statusClass = this.getModelStatusClass(model.status);
    const statusIcon = this.getModelStatusIcon(model.status);

    return `
      <div class="model-item ${statusClass}" title="${this.escapeHtml(model.reason || '')}">
        <div class="model-header">
          <span class="model-icon">${statusIcon}</span>
          <span class="model-name">${this.escapeHtml(model.displayName)}</span>
          <span class="model-id">${this.escapeHtml(model.id)}</span>
        </div>
        ${model.reason ? `<div class="model-reason">${this.escapeHtml(model.reason)}</div>` : ''}
        ${model.metadata ? `
          <div class="model-metadata">
            <span title="Provider">🏢 ${this.escapeHtml(model.metadata.provider)}</span>
            <span title="Quality Score">⭐ ${model.metadata.quality_score}/10</span>
            <span title="Cost per 1M tokens">💰 $${(model.metadata.cost_per_1m / 1000).toFixed(2)}</span>
            <span title="Latency">⚡ ${model.metadata.latency_ms}ms</span>
          </div>
        ` : ''}
      </div>
    `;
  }

  private getModelStatusClass(status: ModelState['status']): string {
    switch (status) {
      case 'allowed': return 'status-allowed';
      case 'filtered': return 'status-filtered';
      case 'fallback': return 'status-fallback';
      default: return '';
    }
  }

  private getModelStatusIcon(status: ModelState['status']): string {
    switch (status) {
      case 'allowed': return '✅';
      case 'filtered': return '❌';
      case 'fallback': return '⚠️';
      default: return '';
    }
  }

  private countTotalModels(stages: ChainStage[]): number {
    return stages.reduce((sum, stage) => sum + stage.models.length, 0);
  }

  private countFilteredModels(stages: ChainStage[]): number {
    return stages.reduce((sum, stage) => sum + stage.filteredModels.length, 0);
  }

  private async verifyChain(): Promise<void> {
    if (!this.currentVisualization) return;

    const taskType = (document.getElementById('task-type') as HTMLSelectElement).value;
    const region = (document.getElementById('region') as HTMLSelectElement).value;
    const tier = (document.getElementById('tier') as HTMLSelectElement).value;
    const preference = (document.getElementById('preference') as HTMLSelectElement).value;

    const verificationContainer = document.getElementById('verification-container');
    if (!verificationContainer) return;

    verificationContainer.style.display = 'block';
    verificationContainer.innerHTML = '<div class="loading">⏳ Making real API request (direct call)...</div>';

    try {
      // Call AI service directly from UI (similar to Single Prompt tab)
      // This avoids the 25-second timeout in admin-service backend
      const testMessage = 'This is a test message for verification.';
      const startTime = Date.now();

      const token = this.apiClient['token'];
      if (!token) {
        throw new Error('No authentication token available');
      }

      // Get the base URL from apiClient - extract the domain
      const baseUrl = this.apiClient['baseUrl'] || 'https://api.consenger.app';

      // Call the restored admin verify endpoint
      // This endpoint now uses internal service bindings to call AI service efficiently
      // and returns the tracking headers (X-Actual-Model) that are strip from public API
      const verifyResponse = await fetch(`${baseUrl}/v1/admin/routing/verify`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          region,
          tier,
          taskType,
          message: testMessage
        }),
      });

      const responseTimeMs = Date.now() - startTime;

      let actualResult: {
        status: 'success' | 'error';
        modelUsed: string;
        provider?: string;
        reason?: string;
        fallbackAttempts?: number;
        responseTimeMs: number;
        response?: string;
        error?: string;
      };

      if (!verifyResponse.ok) {
        const errorBody = await verifyResponse.text();
        actualResult = {
          status: 'error',
          modelUsed: 'unknown',
          error: `Admin Service returned ${verifyResponse.status}: ${errorBody}`,
          responseTimeMs,
        };
      } else {
        const data = await verifyResponse.json();

        actualResult = {
          status: 'success',
          modelUsed: data.modelUsed || 'unknown',
          provider: data.provider || 'unknown',
          reason: 'completed',
          fallbackAttempts: 0,
          responseTimeMs,
          response: data.response || '',
        };
      }

      // For verification, success means the request completed without errors
      // We can't compare exact models because the API doesn't expose which model was actually used
      const predicted = this.currentVisualization;
      const isSuccess = actualResult.status === 'success';

      const differences: string[] = [];
      if (!isSuccess) {
        differences.push(`Error occurred: ${actualResult.error}`);
      }

      const verification: ChainVerificationResponse = {
        predicted,
        actual: actualResult,
        matched: isSuccess, // Matched = request succeeded using the predicted routing chain
        differences,
        timestamp: new Date().toISOString(),
      };

      this.renderVerification(verification);
    } catch (error) {
      verificationContainer.innerHTML = `
        <div class="error">
          <p>❌ Verification failed</p>
          <p class="error-detail">${error instanceof Error ? error.message : 'Unknown'}</p>
        </div>
      `;
    }
  }

  private renderVerification(data: ChainVerificationResponse): void {
    const container = document.getElementById('verification-container');
    if (!container) return;

    const matchIcon = data.matched ? '✅' : '⚠️';
    const matchClass = data.matched ? 'match-success' : 'match-warning';

    container.innerHTML = `
      <div class="verification-result ${matchClass}">
        <h3>${matchIcon} Verification Result</h3>

        <div class="verification-comparison">
          <div class="comparison-item">
            <h4>Predicted Model</h4>
            <div class="model-badge">${this.escapeHtml(data.predicted.finalModel.displayName)}</div>
            <code>${this.escapeHtml(data.predicted.finalModel.id)}</code>
          </div>

          <div class="comparison-arrow">${data.matched ? '=' : '≠'}</div>

          <div class="comparison-item">
            <h4>Actual Model Used</h4>
            ${data.actual.status === 'success' ? `
              <div class="model-badge">${this.escapeHtml(data.actual.modelUsed)}</div>
              <div class="model-details">
                <div>Provider: <code>${this.escapeHtml(data.actual.provider || 'unknown')}</code></div>
                <div>Reason: ${this.escapeHtml(data.actual.reason || 'unknown')}</div>
                <div>Fallback Attempts: ${data.actual.fallbackAttempts || 0}</div>
                <div class="response-time">Response Time: ${data.actual.responseTimeMs}ms</div>
              </div>
              ${data.actual.response ? `
                <div class="sample-response">
                  <strong>Sample Response:</strong>
                  <pre>${this.escapeHtml(data.actual.response)}</pre>
                </div>
              ` : ''}
            ` : `
              <div class="error-badge">❌ Failed</div>
              <div class="error-message">${this.escapeHtml(data.actual.error || 'Unknown error')}</div>
              <div class="response-time">Failed after: ${data.actual.responseTimeMs}ms</div>
            `}
          </div>
        </div>

        ${!data.matched && data.differences ? `
          <div class="differences">
            <h4>⚠️ Differences Detected</h4>
            <ul>
              ${data.differences.map(diff => `<li>${this.escapeHtml(diff)}</li>`).join('')}
            </ul>
          </div>
        ` : ''}

        ${data.actual.routerLogs && data.actual.routerLogs.length > 0 ? `
          <details class="router-logs">
            <summary>📋 Router Logs (${data.actual.routerLogs.length} entries)</summary>
            <pre>${this.escapeHtml(data.actual.routerLogs.join('\n'))}</pre>
          </details>
        ` : ''}

        ${data.actual.sampleResponse ? `
          <details class="sample-response">
            <summary>💬 Sample Response</summary>
            <pre>${this.escapeHtml(data.actual.sampleResponse)}</pre>
          </details>
        ` : ''}
      </div>
    `;
  }

  destroy(): void {
    super.destroy();
    this.currentVisualization = null;
  }
}
